import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {Test} from '../../../data/models/test/test';
import {TestExecution} from '../../../data/models/execution/test-execution';
import {QuestionExecution, QuestionExecutionSummaryDTO} from '../../../data/models/execution/question-execution';
import {QuestionExecutionService} from '../../../services/api/execution/question-execution.service';
import {QuestionExecutionComponent} from '../question-execution/question-execution.component';
import {QUESTION_TYPES} from '../../../../constants/questionTypes';

@Component({
  selector: 'app-questions-execution',
  templateUrl: './questions-execution.component.html',
  styleUrls: ['./questions-execution.component.css']
})
export class QuestionsExecutionComponent implements OnInit {

  @Input() test!: Test;
  @Input() urlCode?: string;
  @Input() testExecution!: TestExecution;

  @Output() finishExecution = new EventEmitter<TestExecution>();

  @ViewChild(QuestionExecutionComponent) currentQuestionExecution?: QuestionExecutionComponent;

  questions: QuestionExecution[] = [];
  allQuestionsRequired = true;

  currentQuestionNumber = 1;
  currentQuestion?: QuestionExecution;

  constructor(
    private testExecutionQuestionService: QuestionExecutionService,
    private snackBar: MatSnackBar,
  ) {
  }

  ngOnInit(): void {
    this.testExecutionQuestionService.getQuestionExecutionSummary(this.testExecution.id, this.urlCode)
        .then((response: QuestionExecutionSummaryDTO) => {

          this.allQuestionsRequired = response.allQuestionsRequired ?? true;

          response.questions.forEach((question: QuestionExecution) => {
            this.questions[question.orderNum] = question;
          });

          if (this.questions.length > 0) {
            const indexWithoutAnswer = this.questions.findIndex(q => !q.answered);
            this.setCurrentQuestionByNumber(indexWithoutAnswer >= 0 ? indexWithoutAnswer + 1 : this.questions.length);
          }
      });
  }

  getQuestionByNumber(questionNumber: number): Promise<QuestionExecution> {
    const question = this.questions[questionNumber - 1];
    return new Promise<QuestionExecution>(resolve => {
      resolve(question);
    });
  }

  setCurrentQuestionByNumber(questionNumber: number, skipAnswerValidation = false): void {
    const promise = skipAnswerValidation
      ? Promise.resolve()
      : (this.currentQuestionExecution?.submitQuestion() ?? Promise.resolve());
    promise.then(() => {
      this.getQuestionByNumber(questionNumber)
        .then(question => {
          this.currentQuestionNumber = questionNumber;
          this.currentQuestion = question;

          if (question.imageName) {
            this.loadImage(question.imageName, questionNumber);
          }
        });
    }).catch(() => {
      if (!skipAnswerValidation) {
        this.showAnswerRequiredNotification();
      }
    });
  }

  onExecutionFinish(): void {
    const promise = this.currentQuestionExecution?.submitQuestion() ?? Promise.resolve();
    promise.then(() => {
      this.finishExecution.emit(this.testExecution);
    }).catch(() => {
      this.showAnswerRequiredNotification();
    });
  }

  onQuestionSelect(questionNumber: number): void {
    this.setCurrentQuestionByNumber(questionNumber);
  }

  onPreviousQuestionSelect(): void {
    this.setCurrentQuestionByNumber(this.currentQuestionNumber - 1, true);
  }

  onNextQuestionSelect(): void {
    this.setCurrentQuestionByNumber(this.currentQuestionNumber + 1);
  }

  onQuestionChange(question: QuestionExecution): void {
    this.questions[question.orderNum] = question;
    question.answered = this.isAnswered(question);
  }

  isAnswered(question: QuestionExecution): boolean {
    switch (question.type.techId) {
      case QUESTION_TYPES.SINGLE_ANSWER:
        return question.options.findIndex(o => o.selected) >= 0;
      case QUESTION_TYPES.MULTIPLE_ANSWER: {
        const selectedCount = question.options.filter(o => o.selected).length;
        const minRequired = question.minAnswers != null ? question.minAnswers : 1;
        return selectedCount >= minRequired;
      }
      case QUESTION_TYPES.TEXT_ANSWER:
        const text = question.answer?.text || '';
        return text.trim().length > 0;
    }
    return false;
  }

  private loadImage(imageName: string, questionNumber: number) {
      this.testExecutionQuestionService.getImage(imageName)
          .subscribe(image => {this.createImage(image, questionNumber);})
  }

  private createImage(image: Blob, questionNumber: number) {
    if (image && image.size > 0) {
      const reader = new FileReader();

      reader.addEventListener("load", () => {
        this.getQuestionByNumber(questionNumber)
            .then(q => q.imageFile = reader.result);
        }, false);

      reader.readAsDataURL(image);
    }
  }

  private showAnswerRequiredNotification(): void {
    const message = this.allQuestionsRequired ? 'Необходимо ответить на вопрос' : 'Ответ не соответствует ограничениям вопроса';
    this.snackBar.open(message, 'Закрыть', {
      duration: 4000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
    });
  }

}
